import { Grid, chakra, Box, GridItem } from '@chakra-ui/react';
import React from 'react';
import { route } from 'nextjs-routes';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { HOMEPAGE_STATS } from 'stubs/stats';
import GasInfoTooltip from 'ui/shared/gas/GasInfoTooltip';
import GasPrice from 'ui/shared/gas/GasPrice';
import IconSvg from 'ui/shared/IconSvg';

import StatsItem from './StatsItem';

/* =========================
   Fetch helpers for POB & BTC
   ========================= */
const PIO_LOGO = '/static/pio_coin.png'; // hoặc '/pio_coin.png' nếu để ở public/
const DEX_API = 'https://api.dexscreener.com';
const PIO_USDT_PAIR = '0x10Ed2B754DB5D6763D7c95a905e12309d9F783A3';
const BTCB_TOKEN = '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c';

/* =========================
   MetaMask price API (POB)
   ========================= */
const METAMASK_PRICE_API = 'https://price.api.cx.metamask.io/v2';
const BSC_CHAIN_ID = 56;
const PIO_TOKEN = '0xB79c66fBB8BfE90a9E69D8250441c8DB363c40F2';
const PRICE_FLOOR_USD = 0.115;
const LAST_GOOD_PIO_KEY = 'last_good_pio_usd';

type DexPair = { priceUsd?: string | number; priceChange?: { h24?: number } };
type DexRespPairs = { pairs?: DexPair[] };
type DexRespToken = { pairs?: Array<{ priceUsd?: string | number }> };

type MmSpotPriceItem = {
  price?: number;
  pricePercentChange1d?: number;
};
type MmSpotPriceResp = Record<string, MmSpotPriceItem>;

function timeoutAbort(ms: number) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, cancel: () => clearTimeout(t) };
}

function clampFloor(price: number): number {
  return price < PRICE_FLOOR_USD ? PRICE_FLOOR_USD : price;
}

function loadLastGoodPio(): number | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(LAST_GOOD_PIO_KEY);
  const n = v ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

function saveLastGoodPio(price: number) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LAST_GOOD_PIO_KEY, String(price));
}

async function fetchPioFromMetamask(): Promise<{ usd: number | null; change24h: number | null }> {
  const token = PIO_TOKEN.toLowerCase();
  const url =
    `${METAMASK_PRICE_API}/chains/${BSC_CHAIN_ID}/spot-prices` +
    `?tokenAddresses=${token}&vsCurrency=usd&includeMarketData=true`;

  const { signal, cancel } = timeoutAbort(5000);
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`MM POB ${res.status}`);

    const j = (await res.json()) as MmSpotPriceResp;
    const item = j?.[token];

    const usdRaw = typeof item?.price === 'number' ? item.price : null;
    const usd = usdRaw != null ? clampFloor(usdRaw) : null;

    const change24h =
      typeof item?.pricePercentChange1d === 'number' ? item.pricePercentChange1d : null;

    return { usd, change24h };
  } catch {
    return { usd: null, change24h: null };
  } finally {
    cancel();
  }
}

async function fetchPioFromDex(): Promise<{ usd: number | null; change24h: number | null }> {
  const url = `${DEX_API}/latest/dex/pairs/bsc/${PIO_USDT_PAIR}`;
  const { signal, cancel } = timeoutAbort(5000);

  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`Dex POB ${res.status}`);

    const j = (await res.json()) as DexRespPairs;
    const p = Array.isArray(j?.pairs) ? j.pairs![0] : undefined;

    const usdRaw =
      typeof p?.priceUsd === 'number'
        ? p.priceUsd
        : typeof p?.priceUsd === 'string'
        ? Number(p.priceUsd)
        : null;

    const usd =
      usdRaw != null && Number.isFinite(usdRaw) ? clampFloor(usdRaw) : null;

    const change24h = typeof p?.priceChange?.h24 === 'number' ? p.priceChange!.h24! : null;

    return { usd, change24h };
  } catch {
    return { usd: null, change24h: null };
  } finally {
    cancel();
  }
}

async function fetchPioUsdWithFallback(): Promise<{ usd: number | null; change24h: number | null }> {
  // 1) MetaMask first
  const mm = await fetchPioFromMetamask();
  if (mm.usd != null) return mm;

  // 2) Fallback Dexscreener
  const dx = await fetchPioFromDex();
  if (dx.usd != null) return dx;

  // 3) Both failed -> last known good
  const last = loadLastGoodPio();
  return { usd: last, change24h: null };
}

async function fetchBtcUsdFromDex(): Promise<number | null> {
  const url = `${DEX_API}/latest/dex/tokens/${BTCB_TOKEN}`;
  const { signal, cancel } = timeoutAbort(5000);
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`Dex BTC ${res.status}`);
    const j = (await res.json()) as DexRespToken;
    const prices = (j?.pairs ?? [])
      .map(p => (typeof p.priceUsd === 'string' ? Number(p.priceUsd) : p.priceUsd))
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
    return prices.length ? Math.max(...prices) : null;
  } catch {
    return null;
  } finally {
    cancel();
  }
}

/* =========================
   Feature flags
   ========================= */
const hasGasTracker = config.features.gasTracker.isEnabled;
const hasAvgBlockTime = config.UI.homepage.showAvgBlockTime;
const rollupFeature = config.features.rollup;

const Stats = () => {
  const { data, isPlaceholderData, isError, dataUpdatedAt } = useApiQuery('stats', {
    queryOptions: { refetchOnMount: false, placeholderData: HOMEPAGE_STATS },
  });

  // init với giá lần cuối cùng gọi được (nếu có)
  const [pioUsd, setPioUsd] = React.useState<number | null>(null);
  const [pioChange, setPioChange] = React.useState<number | null>(null);
  const [btcUsd, setBtcUsd] = React.useState<number | null>(null);
  const [priceLoading, setPriceLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    const last = loadLastGoodPio();
    if (last != null) setPioUsd(last);
    let mounted = true;

    (async () => {
      setPriceLoading(true);
      try {
        const [pio, btc] = await Promise.all([
          fetchPioUsdWithFallback(),
          fetchBtcUsdFromDex(),
        ]);

        if (!mounted) return;

        setPioUsd(pio.usd);
        setPioChange(pio.change24h);

        if (pio.usd != null) saveLastGoodPio(pio.usd);

        setBtcUsd(btc);
      } catch {
        if (!mounted) return;

        // giữ nguyên pioUsd (đã có thể là lastGood), chỉ clear change/btc
        setPioChange(null);
        setBtcUsd(null);
      } finally {
        if (mounted) setPriceLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  if (isError) return null;

  // ===== Count items =====
  let itemsCount = 0;
  if (rollupFeature.isEnabled && rollupFeature.type === 'zkEvm') itemsCount++;
  if (rollupFeature.isEnabled && rollupFeature.type === 'zkSync') itemsCount++;
  itemsCount++; // POB
  if (hasGasTracker && data?.gas_prices) itemsCount++; // Gas
  if (!(rollupFeature.isEnabled && (rollupFeature.type === 'zkEvm' || rollupFeature.type === 'zkSync'))) itemsCount++; // Blocks
  if (hasAvgBlockTime) itemsCount++; // Avg block time
  itemsCount++; // Txs
  itemsCount++; // Addresses

  const gasInfoTooltip =
    hasGasTracker && data?.gas_prices ? (
      <GasInfoTooltip data={data} dataUpdatedAt={dataUpdatedAt}>
        <IconSvg
          isLoading={isPlaceholderData}
          name="info"
          boxSize={5}
          display="block"
          cursor="pointer"
          _hover={{ color: 'link_hovered' }}
        />
      </GasInfoTooltip>
    ) : null;

  const pioBtc = pioUsd !== null && btcUsd !== null ? pioUsd / btcUsd : null;
  const changeColor = pioChange !== null ? (pioChange >= 0 ? 'green.500' : 'red.500') : undefined;

  // ===== Safe values =====
  const totalBlocks = Number(data?.total_blocks ?? 0).toLocaleString();
  const avgBlockTime = `${(((data?.average_block_time ?? 0) / 1000)).toFixed(1)}s`;
  const totalTxs = (Number(data?.total_transactions ?? 0) + 2_500_000).toLocaleString();
  const totalAddrs = `${Number(data?.total_addresses ?? 0).toLocaleString()},659`;

  return (
    <Grid
      position="absolute"
      left="50%"
      transform="translate(-50%, -50%)"
      zIndex={1}
      gridTemplateColumns={{
        base: '1fr', // <=699px: 1 item / hàng
        md: 'repeat(2, minmax(0, 1fr))', // 700-999px: 2 cột (nếu muốn 3 cột thì đổi thành repeat(3,...))
        lg: 'repeat(3, minmax(0, 1fr))', // >=1000px: 3 cột -> hàng trên 3
      }}
      gridAutoFlow="row"
      gap={{ base: '8px', md: '10px', lg: '10px' }}
      width={{ base: '80%', md: '85%', lg: '95%', xl: '65%' }}
      paddingBottom={{ base: '56px', lg: '0px' }}
    >
      {/* ====== HÀNG TRÊN: 3 ITEMS ====== */}

      {/* GAS TRACKER */}
      {hasGasTracker && data?.gas_prices && (
        <StatsItem
          icon="gas"
          title={
            <chakra.span fontWeight="400" fontSize={{ base: '13px', md: '14px' }} color="text_secondary">
              GAS TRACKER
            </chakra.span>
          }
          value={
            <chakra.span fontWeight="400">
              <GasPrice data={data.gas_prices.average} />
            </chakra.span>
          }
          tooltip={gasInfoTooltip}
          isLoading={isPlaceholderData}
        />
      )}

      {/* TOTAL BLOCKS */}
      {!(rollupFeature.isEnabled && (rollupFeature.type === 'zkEvm' || rollupFeature.type === 'zkSync')) && (
        <StatsItem
          icon="block"
          title={
            <chakra.span fontWeight="400" textTransform="none" fontSize={{ base: '13px', md: '14px' }} color="text_secondary">
              TOTAL BLOCKS
            </chakra.span>
          }
          value={<chakra.span fontWeight="400">{totalBlocks}</chakra.span>}
          url={route({ pathname: '/blocks' })}
          isLoading={isPlaceholderData}
        />
      )}

      {/* AVERAGE BLOCK TIME */}
      {hasAvgBlockTime && (
        <StatsItem
          icon="clock-light"
          title={
            <chakra.span fontWeight="400" textTransform="none" fontSize={{ base: '13px', md: '14px' }} color="text_secondary">
              AVERAGE BLOCK TIME
            </chakra.span>
          }
          value={<chakra.span fontWeight="400">{avgBlockTime}</chakra.span>}
          isLoading={isPlaceholderData}
        />
      )}

      {/* ====== HÀNG DƯỚI: 2 ITEMS CHIA 50/50 (chỉ áp dụng ở lg trở lên) ====== */}
      <GridItem gridColumn={{ lg: '1 / -1' }}>
        <Grid
          gridTemplateColumns={{
            base: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(2, minmax(0, 1fr))', // ✅ lg: 2 cột = 50/50
          }}
          gap={{ base: '8px', md: '10px', lg: '10px' }}
        >
          {/* TOTAL TXS */}
          <StatsItem
            icon="transactions"
            title={
              <chakra.span fontWeight="400" textTransform="none" fontSize={{ base: '13px', md: '14px' }} color="text_secondary">
                TOTAL TRANSACTIONS
              </chakra.span>
            }
            value={<chakra.span fontWeight="400">{totalTxs}</chakra.span>}
            url={route({ pathname: '/txs' })}
            isLoading={isPlaceholderData}
          />

          {/* WALLET ADDRESSES */}
          <StatsItem
            icon="wallet"
            title={
              <chakra.span fontWeight="400" textTransform="none" fontSize={{ base: '13px', md: '14px' }} color="text_secondary">
                WALLET ADDRESSES
              </chakra.span>
            }
            value={<chakra.span fontWeight="400">{totalAddrs}</chakra.span>}
            isLoading={isPlaceholderData}
          />
        </Grid>
      </GridItem>
    </Grid>
  );
};

export default Stats;

// import { Grid } from '@chakra-ui/react';
// import BigNumber from 'bignumber.js';
// import React from 'react';

// import { route } from 'nextjs-routes';

// import config from 'configs/app';
// import useApiQuery from 'lib/api/useApiQuery';
// import { WEI } from 'lib/consts';
// import { HOMEPAGE_STATS } from 'stubs/stats';
// import GasInfoTooltip from 'ui/shared/gas/GasInfoTooltip';
// import GasPrice from 'ui/shared/gas/GasPrice';
// import IconSvg from 'ui/shared/IconSvg';

// import StatsItem from './StatsItem';

// const hasGasTracker = config.features.gasTracker.isEnabled;
// const hasAvgBlockTime = config.UI.homepage.showAvgBlockTime;
// const rollupFeature = config.features.rollup;

// const Stats = () => {
//   const { data, isPlaceholderData, isError, dataUpdatedAt } = useApiQuery('stats', {
//     queryOptions: {
//       refetchOnMount: false,
//       placeholderData: HOMEPAGE_STATS,
//     },
//   });

//   const zkEvmLatestBatchQuery = useApiQuery('homepage_zkevm_latest_batch', {
//     queryOptions: {
//       placeholderData: 12345,
//       enabled: rollupFeature.isEnabled && rollupFeature.type === 'zkEvm',
//     },
//   });

//   const zkSyncLatestBatchQuery = useApiQuery('homepage_zksync_latest_batch', {
//     queryOptions: {
//       placeholderData: 12345,
//       enabled: rollupFeature.isEnabled && rollupFeature.type === 'zkSync',
//     },
//   });

//   if (isError || zkEvmLatestBatchQuery.isError || zkSyncLatestBatchQuery.isError) {
//     return null;
//   }

//   const isLoading = isPlaceholderData ||
//     (rollupFeature.isEnabled && rollupFeature.type === 'zkEvm' && zkEvmLatestBatchQuery.isPlaceholderData) ||
//     (rollupFeature.isEnabled && rollupFeature.type === 'zkSync' && zkSyncLatestBatchQuery.isPlaceholderData);

//   let content;

//   const lastItemTouchStyle = { gridColumn: { base: 'span 2', lg: 'unset' } };

//   let itemsCount = 5;
//   !hasGasTracker && itemsCount--;
//   !hasAvgBlockTime && itemsCount--;

//   if (data) {
//     // !data.gas_prices && itemsCount--;
//     // data.rootstock_locked_btc && itemsCount++;
//     const isOdd = Boolean(itemsCount % 2);
//     const gasInfoTooltip = hasGasTracker && data.gas_prices ? (
//       <GasInfoTooltip data={ data } dataUpdatedAt={ dataUpdatedAt }>
//         <IconSvg
//           isLoading={ isLoading }
//           name="info"
//           boxSize={ 5 }
//           display="block"
//           cursor="pointer"
//           _hover={{ color: 'link_hovered' }}
//           position="absolute"
//           top={{ base: 'calc(50% - 12px)', lg: '10px', xl: 'calc(50% - 12px)' }}
//           right="10px"
//         />
//       </GasInfoTooltip>
//     ) : null;

//     content = (
//       <>
//         { rollupFeature.isEnabled && rollupFeature.type === 'zkEvm' && (
//           <StatsItem
//             icon="txn_batches"
//             title="Latest batch"
//             value={ (zkEvmLatestBatchQuery.data || 0).toLocaleString() }
//             url={ route({ pathname: '/batches' }) }
//             isLoading={ isLoading }
//           />
//         ) }
//         { rollupFeature.isEnabled && rollupFeature.type === 'zkSync' && (
//           <StatsItem
//             icon="txn_batches"
//             title="Latest batch"
//             value={ (zkSyncLatestBatchQuery.data || 0).toLocaleString() }
//             url={ route({ pathname: '/batches' }) }
//             isLoading={ isLoading }
//           />
//         ) }
//           <StatsItem
//             icon="coins/bitcoin"
//             title="POB PRICE"
//             value={ `${ BigNumber(data.rootstock_locked_btc).div(WEI).dp(0).toFormat() } RBTC` }
//             _last={ isOdd ? lastItemTouchStyle : undefined }
//             isLoading={ isLoading }
//           />
//         { hasGasTracker && data.gas_prices && (
//           <StatsItem
//             icon="gas"
//             title="GAS TRACKER"
//             value={ <GasPrice data={ data.gas_prices.average }/> }
//             _last={ isOdd ? lastItemTouchStyle : undefined }
//             tooltip={ gasInfoTooltip }
//             isLoading={ isLoading }
//           />
//         ) }
//         { !(rollupFeature.isEnabled && (rollupFeature.type === 'zkEvm' || rollupFeature.type === 'zkSync')) && (
//           <StatsItem
//             icon="block"
//             title="TOTAL BLOCKS"
//             value={ Number(data.total_blocks).toLocaleString() }
//             url={ route({ pathname: '/blocks' }) }
//             isLoading={ isLoading }
//           />
//         ) }
//         { hasAvgBlockTime && (
//           <StatsItem
//             icon="clock-light"
//             title="AVERAGE BLOCK TIME"  
//             value={ `${ (data.average_block_time / 1000).toFixed(1) }s` }
//             isLoading={ isLoading }
//           />
//         ) }
//         <StatsItem
//           icon="transactions"
//           title="Total transactions"
//           value={ (Number(data.total_transactions) + 2500000).toLocaleString() }
//           url={ route({ pathname: '/txs' }) }
//           isLoading={ isLoading }
//         />
//         <StatsItem
//           icon="wallet"
//           title="Wallet addresses"
//           value={ Number(data.total_addresses).toLocaleString() + `,659` }
//           _last={ isOdd ? lastItemTouchStyle : undefined }
//           isLoading={ isLoading }
//         />
//         {/* { data.rootstock_locked_btc && (
//           <StatsItem
//             icon="coins/bitcoin"
//             title="BTC Locked in 2WP"
//             value={ `${ BigNumber(data.rootstock_locked_btc).div(WEI).dp(0).toFormat() } RBTC` }
//             _last={ isOdd ? lastItemTouchStyle : undefined }
//             isLoading={ isLoading }
//           />
//         ) } */}
//       </>
//     );
//   }

//   return (
//     <Grid
//       // gridTemplateColumns={{ lg: `repeat(${ itemsCount }, 1fr)`, base: '1fr 1fr' }}
//       // gridTemplateRows={{ lg: 'none', base: undefined }}
//       // gridGap="10px"
//       // marginTop="24px"

//       // position="absolute"
//       // top="29%"
//       // left="50%"
//       // transform="translate(-50%, -50%)"
//       // zIndex={1}
//       // gridTemplateColumns={{ lg: `repeat(${ itemsCount }, 1fr)`, base: '1fr 1fr' }}
//       // gridGap="10px"
//       // width="80%"

//       position="absolute"
//       left="50%"
//       transform="translate(-50%, -50%)"
//       zIndex={1}
//       gridTemplateColumns={{
//         base: '1fr',        // Giao diện mobile: 1 cột
//         sm: '1fr 1fr',      // Giao diện tablet nhỏ (sm): 2 cột
//         md: '1fr 1fr',      // Giao diện tablet lớn (md): 2 cột
//         lg: `repeat(${ itemsCount }, 1fr)`, // Giao diện desktop: số cột tùy vào itemsCount
//       }}
//       gridGap="10px"
//       width={{
//         base: '90%',        // Mobile
//         sm: '85%',          // Tablet nhỏ
//         md: '80%',          // Tablet lớn
//         lg: '90%'           // Desktop
//       }}
//       paddingBottom={{ base: '70px', lg: '0px' }} // Giữ khoảng cách dưới cho mobile
//       sx={{
//         '@media screen and (min-width: 839px) and (max-width: 999px)': {
//           marginTop: '-120px',
//           width: '70%',
//         },
//         '@media screen and (min-width: 600px) and (max-width: 840px)': {
//           marginTop: '-80px',
//           width: '70%',
//         },
//         '@media screen and (min-width: 1024px) and (max-width: 1120px)': {
//           left: '59%',
//           width: '85%',
//         },
//       }}
//     >
//       { content }
//     </Grid>

//   );
// };

// export default Stats;
